/** Mirrors backend/app/schemas. Keep the two in step. */

/** Also `via:<link path>` ("via:ticket.institution"), and scopes joined by "+" when
 * several roles give different ones (core/permissions.py). */
export type RecordScope = 'all' | 'team' | 'own' | 'none' | (string & {})

/** `via:ticket.institution` → "ticket › institution"; null for any other scope. */
export function viaPath(scope: string): string | null {
  if (!scope.startsWith('via:')) return null
  return scope.slice(4).split('.').map((step) => step.replace(/_/g, ' ')).join(' › ')
}

export interface Page<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export type Level = 'member' | 'manager' | 'admin'

export interface User {
  id: string
  email: string
  name: string
  picture_url: string | null
  is_active: boolean
  is_superuser: boolean
  last_login_at: string | null
  added_at: string
  /** app slug -> the level this person was given; a level everyone holds is not repeated */
  levels: Record<string, Level>
  platform_admin: boolean
  /** May ask the assistant (a paid model); super admins always may. */
  assistant_access: boolean
  connector_access: boolean
}

export interface Me {
  user: User
  /** app slug -> level, including a level everyone holds */
  levels: Record<string, Level>
  platform_admin: boolean
  /** permission code -> record scope */
  permissions: Record<string, RecordScope>
  is_superuser: boolean
  /** The organisation's time zone ("Asia/Kolkata"): every time is shown in it. */
  timezone: string
}

export interface AuthConfig {
  google_enabled: boolean
  dev_login_enabled: boolean
  allowed_domains: string[]
  /** The product name (`APP_NAME`): browser tab and top bar. */
  product_name: string
}

/** GET /api/access: every app, its levels and what each may do. */
export interface AccessApp {
  slug: string
  name: string
  icon: string
  status: 'active' | 'draft' | 'retired'
  /** the level everyone holds without being given one */
  default_level: Level | null
  tables: {
    key: string
    label: string
    /** set when the table belongs to another app */
    from_app: string | null
    /** level -> action -> scope */
    levels: Record<Level, Record<string, RecordScope>>
  }[]
}

export interface AccessOverview {
  levels: { slug: Level; name: string }[]
  apps: AccessApp[]
  people: { app: string; app_name: string; table: string; table_label: string } | null
  can_set_platform_admin: boolean
}

/** GET /api/organisation: what a document's letterhead says. */
export interface Organisation {
  name: string
  address: string
  tax_label: string
  tax_number: string
  email: string
  phone: string
  website: string
  /** "Asia/Kolkata": the zone every date and time on the platform is read in. */
  timezone: string
}

export type AppStatus = 'active' | 'draft' | 'retired'

export interface AppSummary {
  id: string
  slug: string
  name: string
  icon: string
  description: string
  status: AppStatus
  position: number
  /** An integration is an app that also reaches an outside service (integrations.md). */
  kind: 'app' | 'integration'
}

export interface AuditEntry {
  id: string
  at: string
  actor_id: string | null
  actor_email: string | null
  actor_kind: 'user' | 'agent' | 'system'
  action: string
  entity_type: string
  entity_id: string | null
  summary: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  ip: string | null
  request_id: string | null
}

export interface LoginLogEntry {
  id: string
  at: string
  email: string | null
  user_id: string | null
  outcome: 'success' | 'denied' | 'error'
  reason: string | null
  method: string
  ip: string | null
  user_agent: string | null
}

export type NotificationKind = 'info' | 'success' | 'warning' | 'error'

export interface Notification {
  id: string
  kind: NotificationKind
  title: string
  body: string
  link: string | null
  read_at: string | null
  added_at: string
}

export interface SessionRow {
  id: string
  added_at: string
  last_seen_at: string
  idle_expires_at: string
  absolute_expires_at: string
  revoked_at: string | null
  revoked_reason: string | null
  ip: string | null
  user_agent: string | null
}


// --- automation (design.md §P17) ---------------------------------------------------

export interface ScheduleEntry {
  id: string
  slug: string
  name: string
  description: string
  app: string
  table: string
  /** The schedule in words, e.g. "Every day at 09:00 (Asia/Kolkata)". */
  schedule: string
  enabled: boolean
  status: 'active' | 'archived'
  next_run_at: string | null
  last_run_at: string | null
  runs: number
  failed_runs: number
}

export interface WorkflowRunEntry {
  id: string
  workflow_id: string
  workflow_name: string
  workflow_slug: string
  app: string | null
  trigger_event: string
  status: 'running' | 'succeeded' | 'failed' | 'skipped'
  record_id: string | null
  trace: Record<string, unknown>[]
  error: string | null
  started_at: string
  finished_at: string | null
}

export interface AgentTaskEntry {
  id: string
  kind: string
  status: 'queued' | 'running' | 'done' | 'failed'
  attempts: number
  max_attempts: number
  payload: Record<string, unknown>
  result: Record<string, unknown> | null
  error: string | null
  run_after: string
  added_at: string
  finished_at: string | null
}
